import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import '../styles/Modal.css';
import { getEURtoMADRate, convertEURtoMAD, convertMADtoEUR } from '../utils/exchangeRate';

interface FactureZakModalProps {
  show: boolean;
  handleClose: () => void;
  initialData?: any;
}

interface FactureZakData {
  id: string;
  dateCreation: Date;
  dateEcheance: Date;
  nomCompte: string;
  mois: number | string; // Peut être un nombre (1-6) ou "acompte"
  montant: number;
  montantDhs: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface Article {
  id: number;
  texte: string;
  quantite: number;
  prixUnitaire: number;
  prixTotal: number;
}

const FactureZakModal: React.FC<FactureZakModalProps> = ({ show, handleClose, initialData }) => {
  const [formData, setFormData] = useState<Partial<FactureZakData>>({
    dateCreation: new Date(),
    dateEcheance: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000), // +15 jours
    nomCompte: 'M. Zakaria ELORCHE',
    mois: 1,
    montant: 0,
    montantDhs: 0
  });
  const [loading, setLoading] = useState(false);
  const [factureNumber, setFactureNumber] = useState('');
  const [exchangeRate, setExchangeRate] = useState<number>(10.5); // Taux par défaut
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [nextArticleId, setNextArticleId] = useState(1);
  const [montantEURManuel, setMontantEURManuel] = useState<number>(0);
  // Ref pour éviter les réinitialisations non désirées du formulaire
  const isInitializedRef = useRef(false);
  const previousShowRef = useRef(false);

  const loadExchangeRate = useCallback(async () => {
    setIsLoadingRate(true);
    try {
      const rate = await getEURtoMADRate();
      console.log('Taux de change chargé:', rate);
      setExchangeRate(rate);
    } catch (error) {
      console.error('Erreur lors du chargement du taux de change:', error);
      toast.warning('Taux de change non disponible, utilisation du taux par défaut');
      setExchangeRate(10.5); // Utiliser le taux par défaut en cas d'erreur
    } finally {
      setIsLoadingRate(false);
    }
  }, []);

  const generateFactureNumber = useCallback(async () => {
    try {
      const docRef = doc(db, "prospects", "companies");
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const zakariaData = data.ZakariaELORCHE;
        
        if (zakariaData && zakariaData.invoices) {
          // Utiliser la date de création de la facture au lieu de la date actuelle
          const creationDate = formData.dateCreation || new Date();
          const year = creationDate.getFullYear();
          const month = creationDate.getMonth() + 1;
          
          console.log('Génération numéro facture - Date de création:', creationDate);
          console.log('Année extraite:', year, 'Mois extrait:', month);
          
          const sameMonthInvoices = zakariaData.invoices.filter((invoice: any) => {
            const invoiceDate = invoice.dateCreation?.toDate?.() || new Date(invoice.dateCreation);
            return invoiceDate.getFullYear() === year && 
                   invoiceDate.getMonth() + 1 === month;
          });
          
          console.log(`Factures trouvées pour ${year}-${month.toString().padStart(2, '0')}:`, sameMonthInvoices.length);
          
          // Générer le numéro suivant pour ce mois et cette année
          const nextNumber = sameMonthInvoices.length + 1;
          const formattedMonth = month.toString().padStart(2, '0');
          const newFactureNumber = `${year}-${formattedMonth}-${nextNumber.toString().padStart(3, '0')}`;
          
          console.log('Numéro de facture généré:', newFactureNumber);
          setFactureNumber(newFactureNumber);
        } else {
          // Première facture - utiliser la date de création
          const creationDate = formData.dateCreation || new Date();
          const year = creationDate.getFullYear();
          const month = (creationDate.getMonth() + 1).toString().padStart(2, '0');
          const firstFactureNumber = `${year}-${month}-001`;
          console.log('Première facture - Numéro généré:', firstFactureNumber);
          setFactureNumber(firstFactureNumber);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la génération du numéro de facture:', error);
      // Fallback
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      setFactureNumber(`${currentYear}-${currentMonth}-001`);
    }
  }, [formData.dateCreation]);

  // Charger le taux de change quand le modal s'ouvre (séparé pour ne pas réinitialiser le formulaire)
  useEffect(() => {
    if (show) {
      loadExchangeRate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]); // loadExchangeRate est stable (useCallback), pas besoin de le mettre dans les dépendances

  // Initialiser les données du formulaire (séparé du chargement du taux)
  // Ne réinitialiser que quand le modal s'ouvre pour la première fois ou change de mode (ajout/édition)
  useEffect(() => {
    // Réinitialiser le flag quand le modal se ferme
    if (!show) {
      isInitializedRef.current = false;
      previousShowRef.current = false;
      setArticles([]);
      setNextArticleId(1);
      setMontantEURManuel(0);
      return;
    }

    // Si le modal vient de s'ouvrir (transition de false à true)
    const justOpened = !previousShowRef.current && show;
    previousShowRef.current = show;

    // Réinitialiser uniquement si :
    // 1. Le modal vient de s'ouvrir OU
    // 2. Le mode change (de l'édition à l'ajout ou vice versa)
    const shouldInitialize = justOpened || (show && !isInitializedRef.current);

    if (shouldInitialize) {
      if (initialData) {
         // Mode édition : charger les données existantes
         const parseDate = (dateValue: any): Date => {
           if (dateValue instanceof Date) {
             return dateValue;
           }
           if (dateValue && typeof dateValue.toDate === 'function') {
             // C'est un Timestamp Firestore
             return dateValue.toDate();
           }
           if (dateValue && typeof dateValue === 'string') {
             return new Date(dateValue);
           }
           if (dateValue && typeof dateValue === 'number') {
             return new Date(dateValue);
           }
           // Fallback
           return new Date();
         };

         setFormData({
          dateCreation: parseDate(initialData.dateCreation),
          dateEcheance: parseDate(initialData.dateEcheance),
          nomCompte: initialData.nomCompte,
          mois: initialData.mois,
          montant: initialData.montant,
          montantDhs: initialData.montantDhs
        });
        setFactureNumber(initialData.id);
        // Initialiser le montant EUR modifiable avec la valeur existante
        setMontantEURManuel(initialData.montant || 0);
        // Charger les articles si ils existent
        if (initialData.articles && Array.isArray(initialData.articles)) {
          if (initialData.articles.length > 0) {
            setArticles(initialData.articles.map((art: any, idx: number) => ({
              id: art.id || idx + 1,
              texte: art.texte || '',
              quantite: art.quantite || 1,
              prixUnitaire: art.prixUnitaire || 0,
              prixTotal: art.prixTotal !== undefined ? art.prixTotal : ((art.quantite || 1) * (art.prixUnitaire || 0))
            })));
            const maxId = Math.max(...initialData.articles.map((art: any, idx: number) => art.id || idx + 1));
            setNextArticleId(maxId > 0 ? maxId + 1 : 1);
          } else {
            // Tableau vide
            setArticles([]);
            setNextArticleId(1);
          }
        } else {
          // Pas d'articles
          setArticles([]);
          setNextArticleId(1);
        }
       } else {
        // Mode ajout : réinitialiser le formulaire
        const newDateCreation = new Date();
        setFormData({
          dateCreation: newDateCreation,
          dateEcheance: new Date(newDateCreation.getTime() + 15 * 24 * 60 * 60 * 1000),
          nomCompte: 'M. Zakaria ELORCHE',
          mois: 1,
          montant: 0,
          montantDhs: 0
        });
        setArticles([]);
        setNextArticleId(1);
        setMontantEURManuel(0);
        // Générer le numéro de facture après avoir défini la date
        const generateNumber = async () => {
          try {
            const docRef = doc(db, "prospects", "companies");
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const data = docSnap.data();
              const zakariaData = data.ZakariaELORCHE;
              
              if (zakariaData && zakariaData.invoices) {
                const year = newDateCreation.getFullYear();
                const month = newDateCreation.getMonth() + 1;
                
                const sameMonthInvoices = zakariaData.invoices.filter((invoice: any) => {
                  const invoiceDate = invoice.dateCreation?.toDate?.() || new Date(invoice.dateCreation);
                  return invoiceDate.getFullYear() === year && 
                         invoiceDate.getMonth() + 1 === month;
                });
                
                const nextNumber = sameMonthInvoices.length + 1;
                const formattedMonth = month.toString().padStart(2, '0');
                setFactureNumber(`${year}-${formattedMonth}-${nextNumber.toString().padStart(3, '0')}`);
              } else {
                const year = newDateCreation.getFullYear();
                const month = (newDateCreation.getMonth() + 1).toString().padStart(2, '0');
                setFactureNumber(`${year}-${month}-001`);
              }
            }
          } catch (error) {
            console.error('Erreur lors de la génération du numéro de facture:', error);
            const year = newDateCreation.getFullYear();
            const month = (newDateCreation.getMonth() + 1).toString().padStart(2, '0');
            setFactureNumber(`${year}-${month}-001`);
          }
        };
        generateNumber();
      }
      isInitializedRef.current = true;
    }
  }, [show, initialData]); // Retirer generateFactureNumber des dépendances

  // Mettre à jour la date d'échéance et régénérer le numéro de facture quand la date de création change
  useEffect(() => {
    if (formData.dateCreation && !initialData) {
      const echeanceDate = new Date(formData.dateCreation);
      echeanceDate.setDate(echeanceDate.getDate() + 15);
      setFormData(prev => ({
        ...prev,
        dateEcheance: echeanceDate
      }));
      
      // Régénérer le numéro de facture basé sur la nouvelle date de création
      generateFactureNumber();
    }
  }, [formData.dateCreation, initialData, generateFactureNumber]);

  // Calculer le montant total en DHS (somme des prix totaux des articles)
  const montantTotalDHS = useMemo(() => {
    return articles.reduce((total, article) => total + (article.prixTotal || 0), 0);
  }, [articles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    
    try {
      const docRef = doc(db, "prospects", "companies");
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        toast.error('Document Firestore non trouvé');
        return;
      }

      const data = docSnap.data();
      const zakariaData = data.ZakariaELORCHE || {};
      const existingInvoices = zakariaData.invoices || [];

      if (initialData) {
        // Mode édition : mettre à jour la facture existante
        const updatedInvoices = existingInvoices.map((invoice: any) => {
          if (invoice.id === initialData.id) {
            return {
              ...invoice,
              dateCreation: formData.dateCreation!,
              dateEcheance: formData.dateEcheance!,
              nomCompte: formData.nomCompte!,
              mois: formData.mois!,
              montant: montantEURManuel, // Utiliser le montant EUR modifiable
              montantDhs: montantTotalDHS, // Utiliser le montant DHS calculé depuis les articles (somme des prix totaux)
              articles: articles, // Toujours sauvegarder les articles (même si tableau vide)
              updatedAt: Timestamp.now()
            };
          }
          return invoice;
        });

        await updateDoc(docRef, {
          'ZakariaELORCHE.invoices': updatedInvoices
        });

        const articlesCount = articles.length;
        toast.success(`Facture mise à jour avec succès ! ${articlesCount > 0 ? `${articlesCount} article(s) enregistré(s).` : ''}`);
      } else {
        // Mode ajout : créer une nouvelle facture
        const newFacture: any = {
          id: factureNumber,
          dateCreation: formData.dateCreation!,
          dateEcheance: formData.dateEcheance!,
          nomCompte: formData.nomCompte!,
          mois: formData.mois!,
          montant: montantEURManuel, // Utiliser le montant EUR modifiable
          montantDhs: montantTotalDHS, // Utiliser le montant DHS calculé depuis les articles (somme des prix totaux)
          articles: articles, // Toujours sauvegarder les articles (même si tableau vide)
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        const updatedInvoices = [...existingInvoices, newFacture];

        await updateDoc(docRef, {
          'ZakariaELORCHE.invoices': updatedInvoices
        });

        const articlesCount = articles.length;
        toast.success(`Facture ajoutée avec succès ! ${articlesCount > 0 ? `${articlesCount} article(s) enregistré(s).` : ''}`);
      }

      handleClose();
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la facture:', error);
      toast.error('Erreur lors de la sauvegarde de la facture');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (date: Date | null | undefined): string => {
    if (!date) return '';
    try {
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return '';
    }
  };

  const handleInputChange = (field: keyof FactureZakData, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Conversion automatique des montants uniquement si la valeur est valide et supérieure à 0
      if (field === 'montant' && value != null && !isNaN(value) && value > 0) {
        const madValue = convertEURtoMAD(value, exchangeRate);
        updated.montantDhs = madValue;
      } else if (field === 'montantDhs' && value != null && !isNaN(value) && value > 0) {
        const eurValue = convertMADtoEUR(value, exchangeRate);
        updated.montant = eurValue;
      }

      return updated;
    });
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ borderRadius: '12px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', padding: 0 }}>
        <div className="modal-header" style={{ backgroundColor: '#DFE6E9', borderRadius: '12px 12px 0 0', padding: '1.5rem', flexShrink: 0 }}>
          <h2 style={{ fontFamily: 'Montserrat, sans-serif', margin: 0, fontWeight: 'bold', color: '#424273' }}>{initialData ? 'Modifier une facture Zak' : 'Ajouter une facture Zak'}</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div style={{ overflowY: 'auto', flex: 1 }}>
        <form onSubmit={handleSubmit} className="modal-form" style={{ padding: '1.5rem' }}>
                     <div className="form-group">
             <label>Numéro de facture :</label>
             <input
               type="text"
               value={factureNumber}
               readOnly
               className="form-control"
               style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', padding: '0.4rem 0.5rem', height: 'auto' }}
             />
             <small style={{ color: '#666', fontSize: '12px' }}>
               Format automatique : Année-Mois-Numéro (ex: 2024-01-001)
               {initialData && ' - Le numéro ne peut pas être modifié'}
             </small>
           </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Date de création :</label>
              <input
                type="date"
                value={formatDateForInput(formData.dateCreation)}
                onChange={(e) => handleInputChange('dateCreation', new Date(e.target.value))}
                className="form-control"
                style={{ padding: '0.4rem 0.5rem', height: 'auto' }}
                required
              />
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label>Date d'échéance :</label>
              <input
                type="date"
                value={formatDateForInput(formData.dateEcheance)}
                readOnly
                className="form-control"
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', padding: '0.4rem 0.5rem', height: 'auto' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Nom associé au compte :</label>
            <input
              type="text"
              value={formData.nomCompte}
              readOnly
              className="form-control"
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', padding: '0.4rem 0.5rem', height: 'auto' }}
            />
          </div>

          {/* Section Articles */}
          <div className="form-group" style={{ marginTop: '20px', borderTop: '2px solid #dee2e6', paddingTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <button
                type="button"
                onClick={() => {
                  const newArticle: Article = {
                    id: nextArticleId,
                    texte: '',
                    quantite: 1,
                    prixUnitaire: 0,
                    prixTotal: 0
                  };
                  setArticles([...articles, newArticle]);
                  setNextArticleId(nextArticleId + 1);
                }}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  width: '30px',
                  height: '30px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
                title="Ajouter un article"
              >
                +
              </button>
              <label style={{ fontWeight: 'bold', fontSize: '16px', margin: 0 }}>Articles</label>
            </div>

            {articles.map((article, index) => (
              <div key={article.id} style={{ 
                border: '1px solid #dee2e6', 
                borderRadius: '5px', 
                padding: '15px', 
                marginBottom: '15px',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Article {index + 1} :</span>
                  <button
                    type="button"
                    onClick={() => {
                      setArticles(articles.filter(a => a.id !== article.id));
                    }}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      marginLeft: 'auto'
                    }}
                    title="Supprimer cet article"
                  >
                    Supprimer
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '80px 2fr 100px 120px 120px', gap: '10px', marginBottom: '10px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '14px' }}>N° d'article</label>
                    <input
                      type="text"
                      value={`${index + 1} :`}
                      readOnly
                      className="form-control"
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', fontSize: '14px', padding: '0.4rem 0.5rem', height: 'auto' }}
                    />
                  </div>
                  
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '14px' }}>Texte d'article</label>
                    <input
                      type="text"
                      value={article.texte}
                      onChange={(e) => {
                        const updatedArticles = articles.map(a => 
                          a.id === article.id ? { ...a, texte: e.target.value } : a
                        );
                        setArticles(updatedArticles);
                      }}
                      className="form-control"
                      style={{ fontSize: '14px', padding: '0.4rem 0.5rem', height: 'auto' }}
                      placeholder="Description de l'article"
                    />
                  </div>
                  
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '14px' }}>Quantité</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={article.quantite || ''}
                      onChange={(e) => {
                        const quantite = parseFloat(e.target.value) || 0;
                        const prixTotal = quantite * article.prixUnitaire;
                        const updatedArticles = articles.map(a => 
                          a.id === article.id ? { ...a, quantite, prixTotal } : a
                        );
                        setArticles(updatedArticles);
                      }}
                      className="form-control"
                      style={{ fontSize: '14px', padding: '0.4rem 0.5rem', height: 'auto' }}
                    />
                  </div>
                  
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '14px' }}>Prix unité</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={article.prixUnitaire || ''}
                      onChange={(e) => {
                        const prixUnitaire = parseFloat(e.target.value) || 0;
                        const prixTotal = article.quantite * prixUnitaire;
                        const updatedArticles = articles.map(a => 
                          a.id === article.id ? { ...a, prixUnitaire, prixTotal } : a
                        );
                        setArticles(updatedArticles);
                      }}
                      className="form-control"
                      style={{ fontSize: '14px', padding: '0.4rem 0.5rem', height: 'auto' }}
                    />
                  </div>
                  
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '14px' }}>Prix total</label>
                    <input
                      type="number"
                      value={article.prixTotal.toFixed(2)}
                      readOnly
                      className="form-control"
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', fontSize: '14px', padding: '0.4rem 0.5rem', height: 'auto' }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {articles.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#6c757d', 
                fontStyle: 'italic',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px'
              }}>
                Aucun article ajouté. Cliquez sur le bouton "+" pour ajouter un article.
              </div>
            )}
          </div>

          {/* Ligne séparatrice */}
          <hr style={{ 
            margin: '0.5rem 0', 
            border: 'none', 
            borderTop: '2px solid #dee2e6' 
          }} />

          {/* Montants totaux */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Montant Total (Dhs) :</label>
              <input
                type="text"
                value={montantTotalDHS > 0 ? montantTotalDHS.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                readOnly
                className="form-control"
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', padding: '0.4rem 0.5rem', height: 'auto', fontWeight: 'bold' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Somme des prix totaux des articles
              </small>
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label>Montant Total (€) :</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={montantEURManuel || ''}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setMontantEURManuel(value);
                }}
                className="form-control"
                style={{ padding: '0.4rem 0.5rem', height: 'auto', fontWeight: 'bold' }}
                placeholder="0.00"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Modifiable - Le montant DHS sera calculé automatiquement
              </small>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={handleClose}>
              Annuler
            </button>
                         <button type="submit" className="submit-button" disabled={loading}>
               {loading ? 'Sauvegarde en cours...' : (initialData ? 'Mettre à jour' : 'Ajouter la facture')}
             </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default FactureZakModal;
